import React, { useCallback, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import type { ImpactAssessmentRow, ImpactSubmission } from "./types";

const clampSx = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
  wordBreak: "break-word" as const,
  fontSize: 12,
  color: "#4A5565",
};

/** Clamped body text; tooltip only when content overflows the 3-line clamp. */
const ClampedFieldText: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setTruncated(el.scrollHeight > el.clientHeight + 1);
  }, []);

  const handleMouseEnter = useCallback(() => {
    measure();
  }, [measure]);

  return (
    <Tooltip
      title={truncated ? text : ""}
      arrow
      placement="top"
      enterDelay={300}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 420,
            fontSize: 12,
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
          },
        },
      }}
    >
      <Typography ref={ref} onMouseEnter={handleMouseEnter} sx={clampSx}>
        {text}
      </Typography>
    </Tooltip>
  );
};

interface FieldTripletProps {
  analysis: string;
  summary: string;
  recommendation: string;
}

const FieldTriplet: React.FC<FieldTripletProps> = ({
  analysis,
  summary,
  recommendation,
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 2,
      mt: 1.25,
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 11, color: "#99A1AF", mb: 0.5 }}>
        Analysis
      </Typography>
      <ClampedFieldText text={analysis} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: 11, color: "#99A1AF", mb: 0.5 }}>
        Summary
      </Typography>
      <ClampedFieldText text={summary} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: 11, color: "#99A1AF", mb: 0.5 }}>
        Recommendation
      </Typography>
      <ClampedFieldText text={recommendation} />
    </Box>
  </Box>
);

interface ImpactBadgeProps {
  label: string;
  bg: string;
  color: string;
}

const ImpactBadge: React.FC<ImpactBadgeProps> = ({ label, bg, color }) => (
  <Box
    component="span"
    sx={{
      bgcolor: bg,
      color,
      borderRadius: "6px",
      px: 1.25,
      py: 0.35,
      fontSize: 11,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

interface SubmissionBlockProps {
  submission: ImpactSubmission;
}

const SubmissionBlock: React.FC<SubmissionBlockProps> = ({ submission }) => (
  <Box
    sx={{
      border: "1px solid #F3F4F6",
      borderRadius: "8px",
      bgcolor: "#F9FAFB",
      px: 1.75,
      py: 1.5,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box
        title={`Submitted ${submission.time}`}
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "999px",
            bgcolor: submission.avatarBg,
            color: "#fff",
            fontSize: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          {submission.initials}
        </Box>
        <Typography sx={{ fontSize: 12, color: "#101828", fontWeight: 700 }}>
          {submission.name}
        </Typography>
      </Box>
      {submission.hasImpact && submission.impactLabel ? (
        <ImpactBadge
          label={submission.impactLabel}
          bg={submission.impactBg}
          color={submission.impactColor}
        />
      ) : null}
    </Box>
    <FieldTriplet
      analysis={submission.analysis}
      summary={submission.summary}
      recommendation={submission.recommendation}
    />
  </Box>
);

export interface ImpactAssessmentCardProps {
  row: ImpactAssessmentRow;
  expanded: boolean;
  onToggleExpand: () => void;
  onShowAll: () => void;
}

export const ImpactAssessmentCard: React.FC<ImpactAssessmentCardProps> = ({
  row,
  expanded,
  onToggleExpand,
  onShowAll,
}) => (
  <Box
    sx={{
      border: "1px solid #E5E7EB",
      borderRadius: "10px",
      bgcolor: "#fff",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.125, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, color: "#99A1AF" }}>
            {row.dimension}
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#101828" }}>
            {row.tagValue}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onToggleExpand}
          aria-label={expanded ? "Collapse row" : "Expand row"}
          sx={{ color: "#99A1AF", p: 0.25 }}
        >
          {expanded ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </IconButton>
      </Box>
      <IconButton
        size="small"
        title="Show all"
        aria-label="Show all"
        onClick={onShowAll}
        sx={{ color: "#6A7282" }}
      >
        <OpenInFullIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>

    {expanded ? (
      <Box
        sx={{
          px: 1.5,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            border: "1px solid #F3F4F6",
            borderRadius: "8px",
            bgcolor: "#F9FAFB",
            px: 1.75,
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography
              title={row.triggeredBy}
              sx={{ fontSize: 11, color: "#6A7282", fontWeight: 600 }}
            >
              {row.triggeredBy}
            </Typography>
            {row.hasImpact && row.impactLabel ? (
              <ImpactBadge
                label={row.impactLabel}
                bg={row.impactBg}
                color={row.impactColor}
              />
            ) : null}
          </Box>
          <FieldTriplet
            analysis={row.analysis}
            summary={row.summary}
            recommendation={row.recommendation}
          />
        </Box>
        {row.submissions.map((sub) => (
          <SubmissionBlock key={sub.id} submission={sub} />
        ))}
      </Box>
    ) : null}
  </Box>
);
